import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useApiFetch } from '../../shared/api'
import type { DraftHuntDetail } from './useDraftHunt'
import type { MemberInvite } from '../teams/inviteInput'

interface DraftBody {
  huntName: string | null
  startTime: string | null
  endTime: string | null
  leaderIds: string[]
  lastStepIndex: number
  /** Guests invited by email or phone, persisted on the plan so a reopened draft
   *  shows them. Each is `{ channel, value }`, the same shape the InviteHuntGuest
   *  send uses. Optional so a caller that never touches guests omits the field
   *  (the API then leaves the stored guest rows alone on update). */
  guests?: MemberInvite[]
  /** Whether hunters see only the planned drives on the map (true) or the whole
   *  hunting ground (false, the default). Set by the "What hunters see on the
   *  map" toggle (estate hunts). The .NET API derives the per-area/per-pin filter
   *  from the drives themselves — the retired `estateMapFilter` is no longer
   *  sent. Always sent so it round-trips every save. */
  restrictMapToPlannedDrives: boolean
}

export interface PublishedHuntDetail {
  id: string
  groupId: string
  status: string
  invitesSent: number
  joinCode?: string | null
}

export function useCreateDraftHunt() {
  const call = useApiFetch()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ groupId, body }: { groupId: string; body: DraftBody }) =>
      call<DraftHuntDetail>(
        `/clubhouse/groups/${encodeURIComponent(groupId)}/hunts/drafts`,
        { method: 'POST', body: JSON.stringify(body) },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['clubhouse', 'me', 'hunts', 'drafts'] }),
  })
}

export function useUpdateDraftHunt() {
  const call = useApiFetch()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ groupId, draftId, body }: { groupId: string; draftId: string; body: DraftBody }) =>
      call<DraftHuntDetail>(
        `/clubhouse/groups/${encodeURIComponent(groupId)}/hunts/drafts/${encodeURIComponent(draftId)}`,
        { method: 'PUT', body: JSON.stringify(body) },
      ),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['clubhouse', 'groups', vars.groupId, 'hunts', 'drafts', vars.draftId] })
      void qc.invalidateQueries({ queryKey: ['clubhouse', 'me', 'hunts', 'drafts'] })
    },
  })
}

export function useDeleteDraftHunt() {
  const call = useApiFetch()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ groupId, draftId }: { groupId: string; draftId: string }) => {
      await call<undefined>(
        `/clubhouse/groups/${encodeURIComponent(groupId)}/hunts/drafts/${encodeURIComponent(draftId)}`,
        { method: 'DELETE' },
      )
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['clubhouse', 'me', 'hunts', 'drafts'] }),
  })
}

export function useFinalizeDraftHunt() {
  const call = useApiFetch()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ groupId, draftId }: { groupId: string; draftId: string }) =>
      call<PublishedHuntDetail>(
        `/clubhouse/groups/${encodeURIComponent(groupId)}/hunts/drafts/${encodeURIComponent(draftId)}/finalize`,
        { method: 'POST' },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['clubhouse', 'me', 'hunts', 'upcoming'] })
      void qc.invalidateQueries({ queryKey: ['clubhouse', 'me', 'hunts', 'drafts'] })
    },
  })
}
